import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { confirm as confirmApi } from '@/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import {
	FieldError,
	FieldGroup,
	FieldLegend,
	FieldSet,
} from '@/components/ui/field';
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemGroup,
	ItemMedia,
	ItemTitle,
} from '@/components/ui/item';
import { Spinner } from '@/components/ui/spinner';
import { useStrings, t } from '@/hooks/useStrings';
import type { LocaleStrings, Submission } from '@/types';
import { Badge } from './components/ui/badge';

type Props = {
	submission: Submission;
	onSuccess: (confirmedAt: string | null) => void;
	onError: () => void;
	onBack: () => void;
};

const FORM_ID = 'mocy-rev-step2-form';

const formatCurrency = (amount: string | number | undefined, currency: string): string => {
	if (amount === undefined || amount === '') return '';
	const numeric = typeof amount === 'number' ? amount : parseFloat(amount);
	if (Number.isNaN(numeric)) return String(amount);
	try {
		return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR' }).format(numeric);
	} catch {
		return `${numeric.toFixed(2)} ${currency || ''}`.trim();
	}
};

const buildSchema = (strings: LocaleStrings) =>
	z.object({
		item_ids: z
			.array(z.number().int().positive())
			.min(1, t(strings, 'error_select_item', 'Bitte wählen Sie mindestens eine Position aus.')),
	});

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export const Step2 = ({ submission, onSuccess, onError, onBack }: Props) => {
	const strings = useStrings();
	const schema = buildSchema(strings);
	const lineItems = submission.line_items || [];
	const selectableItems = useMemo(() => lineItems.filter((i) => !i.already_revoked), [lineItems]);
	const [formError, setFormError] = useState('');

	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			item_ids: [],
		},
	});

	const onSubmit = async (values: FormValues) => {
		setFormError('');
		try {
			const response = await confirmApi({
				order_id: submission.order_id,
				token: submission.token,
				item_ids: values.item_ids,
			});

			if ('ok' in response && response.ok === true) {
				onSuccess(response.confirmed_at ?? null);
				return;
			}

			if ('message' in response && response.message) {
				setFormError(response.message);
				return;
			}
			onError();
		} catch (e) {
			console.error('[mocy-revocation] Step2 threw', e);
			onError();
		}
	};

	const submitting = form.formState.isSubmitting;

	if (submitting) {
		return (
			<Card aria-busy="true" aria-live="polite">
				<CardHeader>
					<CardTitle>{t(strings, 'step2_processing_title', 'Widerruf wird verarbeitet')}</CardTitle>
					<CardDescription>
						{t(strings, 'step2_processing_description', 'Bitte warten Sie einen Moment — Ihr Widerruf wird gespeichert und die Eingangsbestätigung per E-Mail versendet.')}
					</CardDescription>
				</CardHeader>
				<CardContent className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:py-10">
					<Spinner className="tw:size-10! tw:text-muted-foreground!" />
					<span className="tw:sr-only">
						{t(strings, 'submitting', 'Wird gesendet…')}
					</span>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t(strings, 'step2_title', 'Widerruf bestätigen')}</CardTitle>
				<CardDescription>
					{t(strings, 'step2_intro', 'Prüfen Sie Ihre Angaben und wählen Sie die Positionen, die Sie widerrufen möchten.')}
				</CardDescription>
			</CardHeader>

			<CardContent>
				<form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} noValidate>
					<FieldGroup>
						{formError ? (
							<Alert variant="destructive" aria-live="polite">
								<AlertDescription>{formError}</AlertDescription>
							</Alert>
						) : null}
						<ItemGroup>
							<Item variant="muted" size="sm">
								<ItemContent>
									<ItemTitle>{t(strings, 'label_order', 'Bestellnummer')}: {submission.order_number || submission.order_id}</ItemTitle>
								</ItemContent>
							</Item>
							<Item variant="muted" size="sm">
								<ItemContent>
									<ItemTitle>{t(strings, 'label_name', 'Name')}: {submission.name}</ItemTitle>
								</ItemContent>
							</Item>
							<Item variant="muted" size="sm">
								<ItemContent>
									<ItemTitle>{t(strings, 'label_email', 'E-Mail-Adresse')}: {submission.email}</ItemTitle>
								</ItemContent>
							</Item>
						</ItemGroup>

						<Controller
							name="item_ids"
							control={form.control}
							render={({ field, fieldState }) => (
								<FieldSet>
									<FieldLegend variant="label">
										{t(strings, 'items_legend', 'Zu widerrufende Positionen')}
									</FieldLegend>
									<ItemGroup>
										{lineItems.map((item) => {
											const disabled = item.already_revoked;
											const priceLabel = formatCurrency(item.total, submission.currency);
											const inputId = `mocy-rev-item-${item.id}`;
											const checked = field.value.includes(item.id);
											return (
												<Item
													key={item.id}
													asChild
													size="sm"
													data-disabled={disabled ? true : undefined}
												>
												 	<label htmlFor={inputId} className='tw:cursor-pointer!'>
														<ItemMedia>
															<Checkbox
																id={inputId}
																checked={checked}
																onCheckedChange={(next) => {
																	const nextValue = next
																		? [...field.value, item.id]
																		: field.value.filter((v) => v !== item.id);
																	field.onChange(nextValue);
																}}
																disabled={disabled}
																aria-invalid={fieldState.invalid}
															/>
														</ItemMedia>
														<ItemContent className='tw:w-full!'>
															<ItemTitle className={disabled ? 'tw:line-through tw:opacity-30' : ''}>
																{item.quantity}× {item.name}
															</ItemTitle>
															{priceLabel || disabled ? (
																<ItemDescription>
																	<span className={disabled ? 'tw:line-through tw:opacity-30' : ''}>{priceLabel}</span>
																	{disabled ? (
																		<Badge variant="destructive" className='tw:flex! tw:mt-3!'>
																			{t(strings, 'item_already_revoked', 'Bereits Widerrufen')}
																		</Badge>
																	) : null}
																</ItemDescription>
															) : null}
														</ItemContent>
													</label>
												</Item>
											);
										})}
									</ItemGroup>
									{fieldState.invalid && fieldState.error ? (
										<FieldError errors={[fieldState.error]} />
									) : null}
									{selectableItems.length === 0 ? (
										<Alert variant="destructive">
											<AlertDescription>
												{t(strings, 'error_no_open_items', 'Für diese Bestellung stehen keine weiteren Positionen zum Widerruf zur Verfügung.')}
											</AlertDescription>
										</Alert>
									) : null}
								</FieldSet>
							)}
						/>

						<Alert>
							<AlertDescription>
								{t(strings, 'step2_disclaimer', 'Diese Erklärung bestätigt ausschließlich den Eingang Ihres Widerrufs nach § 356a BGB. Sie stellt kein Anerkenntnis der materiellen Wirksamkeit und keine automatische Rückabwicklung dar. Die weitere Bearbeitung erfolgt durch unser Service-Team. Sie erhalten eine Eingangsbestätigung per E-Mail.')}
							</AlertDescription>
						</Alert>
					</FieldGroup>
				</form>
			</CardContent>

			<CardFooter className="tw:gap-2">
				<Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
					{t(strings, 'back', 'Zurück')}
				</Button>
				<Button
					form={FORM_ID}
					type="submit"
					variant="outline"
					disabled={submitting || selectableItems.length === 0}
					aria-busy={submitting}
				>
					{submitting
						? t(strings, 'submitting', 'Wird gesendet…')
						: t(strings, 'submit_step2', 'Widerruf bestätigen')}
				</Button>
			</CardFooter>
		</Card>
	);
};
